import React, { useEffect, useState } from 'react';
import { Icon, Avatar } from '../../../components/ui';
import { usePhone } from '../PhoneContext';
import { fetchCalls } from '../api';
import { CallLogRow } from '../types';
import { normalizePhoneNumber } from '../phoneNumber';
import { isMissed } from '../callMeta';
import { relativeTime } from '../../../utils/relativeTime';

/**
 * RecentCallsPanel — the rep's own last calls, beside the dialpad.
 *
 * Redialling someone you just spoke to is the most common reason to open
 * the dialer, and it previously meant retyping the number. One tap here
 * places the call.
 */

export const RecentCallsPanel: React.FC = () => {
  const { startCall, registration, call } = usePhone();
  const [rows, setRows] = useState<CallLogRow[] | null>(null);

  useEffect(() => {
    let alive = true;
    fetchCalls({ mine: true, page: 1, pageSize: 8 })
      .then(r => { if (alive) setRows(r.calls || []); })
      .catch(() => { if (alive) setRows([]); });
    return () => { alive = false; };
  }, []);

  const busy = registration !== 'registered' || !!call;

  return (
    <div className="w-full sm:w-[260px] shrink-0 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-2xl p-4 flex flex-col max-h-[420px]">
      <div className="flex items-center gap-1.5 mb-3">
        <Icon name="history" size={16} className="text-slate-400" />
        <span className="font-display text-sm font-bold text-slate-500">שיחות אחרונות</span>
      </div>

      {rows === null ? (
        <div className="py-6 text-center text-xs text-slate-400">טוען…</div>
      ) : rows.length === 0 ? (
        <div className="py-6 text-center text-xs text-slate-400">אין שיחות אחרונות</div>
      ) : (
        <div className="overflow-y-auto -mx-1">
          {rows.map(c => {
            const missed = isMissed(c);
            const label = c.customer?.fullName || c.number;
            return (
              <button
                key={c.id}
                onClick={() => { if (!busy) startCall(normalizePhoneNumber(c.number) || c.number); }}
                disabled={busy}
                title={busy ? 'הטלפון לא זמין' : `התקשר ל-${label}`}
                className="w-full flex items-center gap-2 px-2 py-2 rounded-xl hover:bg-slate-50 dark:hover:bg-slate-800 disabled:opacity-40 transition text-start"
              >
                {c.customer
                  ? <Avatar name={c.customer.fullName} size="xs" />
                  : (
                    <span className="size-6 shrink-0 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center">
                      <Icon name="person" size={13} className="text-slate-400" />
                    </span>
                  )}

                <span className="min-w-0 flex-1">
                  <span className="block text-xs font-semibold text-slate-800 dark:text-slate-100 truncate">
                    {label}
                  </span>
                  <span className="flex items-center gap-1 text-[10px] text-slate-400">
                    {/* Arrow direction reads as in/out; RTL doesn't flip it. */}
                    <Icon
                      name={c.direction === 'inbound' ? 'call_received' : 'call_made'}
                      size={11}
                      className={missed ? 'text-red-500' : 'text-slate-400'}
                    />
                    <span className={missed ? 'text-red-500 font-semibold' : ''}>
                      {relativeTime(c.startedAt)}
                    </span>
                  </span>
                </span>

                <Icon name="call" size={15} className="text-emerald-500 shrink-0" />
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};
